/* lib/urlMask.js — short address-bar URLs (2026-09-07 user: "make the URL just
 * domain/store/client, not the long id — run it in the back, don't show it").
 *
 * The app keeps routing on the REAL path (/store/clients/<uuid>); only the
 * address bar shows the DISPLAY path (/store/client). The real path travels in
 * the history entry's state (lhcReal), which the browser keeps across reload and
 * back/forward, so those still land on the exact record. Pure helpers here;
 * the DOM work is in components/UrlMask.jsx. */

export const MASKS = [
  [/^\/store\/clients\/[^/]+$/, "/store/client"],
  [/^\/store\/projects\/[^/]+\/report$/, "/store/report"],
  [/^\/store\/projects\/[^/]+$/, "/store/project"],
  [/^\/admin\/applications\/[^/]+$/, "/admin/application"],
  [/^\/superadmin\/applications\/[^/]+$/, "/admin/application"],
  [/^\/sales\/orders\/[^/]+$/, "/sales/order"],
  [/^\/studio\/[^/]+(\/.*)?$/, "/studio"],
];

/** the short path the address bar should show for a real path, or null when it is already short */
export function maskFor(pathname) {
  const p = String(pathname || "");
  for (const [re, display] of MASKS) {
    const m = re.exec(p);
    if (m) {
      const d = display.replace(/\$(\d)/g, (_, i) => m[Number(i)] || "");
      return d === p ? null : d;
    }
  }
  return null;
}

/** split "/a/b?x=1#h" → { path, rest } */
export function splitUrl(url) {
  const s = String(url || "");
  const i = s.search(/[?#]/);
  return i < 0 ? { path: s, rest: "" } : { path: s.slice(0, i), rest: s.slice(i) };
}

/** the real URL (path + query + hash) behind the current entry, for code that reads window.location directly */
export function realUrlNow(win = typeof window !== "undefined" ? window : null) {
  const st = win && win.history && win.history.state;
  return st && typeof st.lhcReal === "string" ? st.lhcReal : (win ? win.location.pathname + win.location.search + win.location.hash : "");
}

/** the real URL to restore for a display path, given the history entry's state — or null */
export function realFromState(state, displayPath) {
  const real = state && typeof state.lhcReal === "string" ? state.lhcReal : null;
  if (!real) return null;
  return maskFor(splitUrl(real).path) === displayPath ? real : null;
}

/** Hook the browser history so EVERY URL write (React Router's, the studio's own, anyone's)
 *  reaches the address bar ALREADY MASKED: pushState/replaceState receive the display path,
 *  the real URL rides in the entry's state (lhcReal). Nothing is deferred — the bar never
 *  holds the real URL, not even for one task (2026-09-12 user: "the URL shows for a split
 *  second — it must not show at all"). React Router keeps routing on the REAL path because
 *  <BrowserRouter window={routerWindowFor()}> reads its location from lhcReal (below).
 *  Idempotent; returns the uninstall function. */
export function installHistoryMask(win = typeof window !== "undefined" ? window : null) {
  if (!win || !win.history || win.history.__lhcMasked) return () => {};
  const h = win.history;
  const origPush = h.pushState.bind(h), origReplace = h.replaceState.bind(h);
  /** the masked (state, url) for a write, or null when it must pass through untouched */
  const plan = (state, url) => {
    let target = null;
    try { target = new URL(url == null ? win.location.href : String(url), win.location.href); } catch (_) { return null; }
    if (target.origin !== win.location.origin) return null;            // cross-origin: let the browser throw as before
    const prevReal = h.state && typeof h.state.lhcReal === "string" ? h.state.lhcReal : null;
    const display = maskFor(target.pathname);
    let real = null;
    if (display) real = target.pathname + target.search + target.hash;  // a record route → mask it
    else if (url == null && prevReal) real = prevReal;                   // state-only write (RR's idx stamp) → keep the record verbatim
    // a write that stays on a display path (a non-router writer adding "?present=1") keeps the real record
    else if (prevReal && maskFor(splitUrl(prevReal).path) === target.pathname) real = splitUrl(prevReal).path + target.search + target.hash;
    if (!real) return null;
    // the bar shows ONLY the short path — no id, no query (2026-09-07 "just domain/store/client")
    return { state: { ...(state && typeof state === "object" ? state : {}), lhcReal: real }, url: display || target.pathname };
  };
  const wrap = (fn) => function maskedHistoryWrite(state, title, url) {
    const p = plan(state, url);
    return p ? fn(p.state, title, p.url) : fn(state, title, url);
  };
  h.pushState = wrap(origPush);
  h.replaceState = wrap(origReplace);
  h.__lhcMasked = true;
  return () => { h.pushState = origPush; h.replaceState = origReplace; delete h.__lhcMasked; };
}

/** The `window` React Router routes on: the real one, except that `location` is the REAL
 *  URL behind the masked entry (lhcReal), so <BrowserRouter window={…}> keeps matching
 *  /studio/:projectId while the bar shows /studio. A stateless landing on a display path
 *  (typed / shared short link) routes as-is → the <Unmask> fallback routes still apply. */
export function routerWindowFor(win = typeof window !== "undefined" ? window : null) {
  if (!win) return undefined;
  return {
    get location() {
      let u;
      try { u = new URL(realUrlNow(win), win.location.href); } catch (_) { return win.location; }
      return {
        pathname: u.pathname, search: u.search, hash: u.hash, href: u.href,
        origin: win.location.origin, protocol: u.protocol, host: u.host, hostname: u.hostname, port: u.port,
        assign: (x) => win.location.assign(x), replace: (x) => win.location.replace(x), reload: () => win.location.reload(),
        toString: () => u.href,
      };
    },
    get history() { return win.history; },
    get document() { return win.document; },
    addEventListener: (...a) => win.addEventListener(...a),
    removeEventListener: (...a) => win.removeEventListener(...a),
  };
}
